import React from 'react';
import { PropTypes } from 'prop-types';
import { useField } from 'formik';
import styled from 'styled-components';
import classnames from 'classnames';
import Select, { components } from 'react-select';

import Asterisk from '../Asterisk/index';
import LoadingInline from '@molecules/LoadingInline';
import CloseIcon from '@icons/CloseIcon';
import MagnifyingGlassIcon from '@icons/MagnifyingGlassIcon';
import { BodyMedium, ErrorText, LabelLarge } from '@styled';
import fonts from '@general/fonts';
import colors from '@general/colors';

const fontStyling = {
    color: colors.secondary,
    fontFamily: fonts.sans,
    fontWeight: '500',
    letterSpacing: '0.31px',
    lineHeight: '25px',
    fontSize: '18px',
};

const colorStyles = {
    control: (styles, { isActive, isFocused }) => ({
        ...styles,
        borderRadius: 0,
        boxShadow: (isActive || isFocused) && '0px 0px 0px 3px #246DFF1A',
        minHeight: '56px',
        padding: '4px 4.5px',
    }),
    input: (styles, { hasValue, isActive, isFocused }) => ({
        ...styles,
        borderColor: isActive || isFocused ? colors.azure : colors.lightGray,
        position: hasValue ? 'relative' : 'absolute',
        left: hasValue ? 'unset' : '20px',
    }),
    menu: styles => ({
        ...styles,
        boxShadow: '0 2px 16px rgba(0, 0, 0, 0.14);',
        marginTop: '0'
    }),
    menuList: styles => ({
        ...styles,
        marginLeft: '7px',
        marginRight: '7px',
    }),
    valueContainer: styles => ({
        ...styles,
        minHeight: '18px'
    }),
    multiValue: styles => ({
        ...styles,
        backgroundColor: 'rgba(167, 197, 255, 0.1)',
        borderRadius: '8px',
        padding: '8px 12px',
        marginRight: '8px',
    }),
    multiValueLabel: styles => ({
        ...styles,
        ...fontStyling,
        marginRight: '18px',
    }),
    multiValueRemove: styles => ({
        ...styles,
        color: colors.azure,
        ':hover': {
            cursor: 'pointer',
        },
        fontSize: '18px',
        scale: '1.75',
    }),
    option: (styles, { isDisabled, isFocused }) => ({
        ...styles,
        backgroundColor: isFocused && colors.ultraLightGray,
        ...fontStyling,
        color: isDisabled ? colors.lightGray : colors.secondary,
        paddingLeft: '18px',
        paddingRight: '18px',
    }),
    placeholder: styles => ({ ...styles, color: colors.lightGray, marginLeft: '1rem' }),
};

const CustomMultiValueRemove = props => {
    return (
        <components.MultiValueRemove {...props}>
            <CloseIcon fill={colors.azure} height="11px" width="11px" />
        </components.MultiValueRemove>
    );
};

const CustomPlaceholder = ({ placeholder }) => {
    const placeholderContainerStyles = {
        display: 'flex',
        alignItems: 'center',
    };
    return (
        <div style={placeholderContainerStyles}>
            <MagnifyingGlassIcon fill={colors.darkGray} />
            <BodyMedium style={{ color: colors.mediumGray }} className="ml-3">
                {placeholder}
            </BodyMedium>
        </div>
    );
};

const CustomMenuList = ({ children, selectProps, ...rest }) => {
    const menuListHeaderStyle = {
        color: colors.darkGray,
        fontFamily: fonts.sans,
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: '12px',
        letterSpacing: '0.31px',
        padding: '11px 25px 12px 18px'
    };
    return (
        <components.MenuList {...rest}>
            <div style={menuListHeaderStyle}>Co-workers at {selectProps.companyName}</div>
            {children}
        </components.MenuList>
    );
};

const StyledLabel = styled.label`
    display: block;
    margin-top: 8px;
`;

const SelectCompanyRoster = ({
    label,
    options,
    isDisabled = false,
    isLoading = false,
    isOptionDisabled = () => {},
    menuIsOpen,
    noOptionsMessage = 'No options',
    onChange,
    ...props
}) => {
    const [meta] = useField(props);
    const { companyName, id, name, placeholder, required } = props;

    const hasError = meta.touched && meta.error;
    const groupClasses = classnames('form-group');

    return (
        <div className={groupClasses}>
            <StyledLabel htmlFor={id || name}>
                <LabelLarge>
                    {label} {required && <Asterisk />}
                </LabelLarge>
            </StyledLabel>

            {isLoading && <LoadingInline />}
            {!isLoading && (
                <Select
                    companyName={companyName}
                    components={{
                        Placeholder: (() => <CustomPlaceholder placeholder={placeholder}/>),
                        MenuList: CustomMenuList,
                        MultiValueRemove: CustomMultiValueRemove,
                        DropdownIndicator: () => null,
                        IndicatorSeparator: () => null,
                    }}
                    isDisabled={isDisabled}
                    isClearable
                    isMulti
                    isOptionDisabled={isOptionDisabled}
                    isSearchable
                    menuIsOpen={menuIsOpen}
                    name={name}
                    noOptionsMessage={() => <ErrorText>{noOptionsMessage}</ErrorText>}
                    onChange={onChange}
                    options={options}
                    styles={colorStyles}
                />
            )}
            {hasError ? <div className="error-msg">{meta.error}</div> : null}
        </div>
    );
};

SelectCompanyRoster.propTypes = {
    companyName: PropTypes.string.isRequired,
    label: PropTypes.string,
    id: PropTypes.string,
    menuIsOpen: PropTypes.bool,
    name: PropTypes.string,
    onChange: PropTypes.func,
    options: PropTypes.array,
    required: PropTypes.bool,
    isDisabled: PropTypes.bool,
    isLoading: PropTypes.bool,
    isOptionDisabled: PropTypes.func,
    value: PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.any,
    }),
    noOptionsMessage: PropTypes.string,
};

export default SelectCompanyRoster;
